import { type FC, memo, useState } from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button, color, ConfirmationModal, ConfirmationModalType, Icon, Table } from '@wf/components';

import EmptyTableContent from '../../../../components/EmptyTableContent';
import TableWrapper from '../../../../components/TableWrapper';
import { useLicenseContext } from '../../LicenseModeContext';
import TableFooter from '../../../../components/TableFooter';
import AdditionalFields from '../AdditionalFields';
import BeneficiaryPopup from './BeneficiaryPopup';

import { FieldPrefix } from '../../constants/fieldPrefixes';
import { ArrayFields } from '../../types/fieldEnums';
import { Tab } from '../../types/enums';

import { beneficiariesSelector, featureFlagsSelector } from '../../store/selectors';

import styles from './Beneficiaries.module.scss';

const Beneficiaries: FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { viewMode } = useLicenseContext();
  const beneficiaries = useSelector(beneficiariesSelector);
  const featureFlags = useSelector(featureFlagsSelector);
  const [type, setType] = useState<Nullable<'add' | 'update'>>(null);

  const [deletingBeneficiaryIndex, setDeletingBeneficiaryIndex] = useState<Nullable<number>>(null);
  const [selectedBeneficiaryIndex, setSelectedBeneficiaryIndex] = useState<Nullable<number>>(null);

  const { append, remove } = useFieldArray({ name: ArrayFields.beneficiaries, control, shouldUnregister: false });

  const editBeneficiary = (index: number) => {
    setSelectedBeneficiaryIndex(index);
    setType('update');
  };

  const deleteBeneficiary = () => {
    if (deletingBeneficiaryIndex) {
      remove(deletingBeneficiaryIndex);
    }

    setDeletingBeneficiaryIndex(null);
  };

  const openAddPopup = () => {
    const index = beneficiaries.length;
    append({
      code: null,
      description: null,
      phoneNumber: null,
      email: '',
    });
    setSelectedBeneficiaryIndex(index - 1);
    setType('add');
  };

  const closeAddPopup = () => {
    remove(selectedBeneficiaryIndex as number);
    setSelectedBeneficiaryIndex(null);
  };

  const handleChange = () => {
    setSelectedBeneficiaryIndex(null);
  };

  if (!featureFlags?.listOfBeneficiariesEnabled) {
    return null;
  }

  return (
    <>
      <TableWrapper
        actions={
          <Button leftIcon={<Icon name="ic_add" />} onClick={openAddPopup}>
            {t('Add New Beneficiary')}
          </Button>
        }
        title={t('List Of Beneficiaries')}
      >
        <Table>
          <Table.Grid>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell className={styles.firstCell}>{t('#')}</Table.HeaderCell>
                <Table.HeaderCell className={styles.cell}>{t('Beneficiary Code')}</Table.HeaderCell>
                <Table.HeaderCell className={styles.cell}>{t('Beneficiary Name & Address')}</Table.HeaderCell>
                <Table.HeaderCell className={styles.cell}>{t('Beneficiary Phone')}</Table.HeaderCell>
                <Table.HeaderCell className={styles.cell}>{t('Beneficiary Email')}</Table.HeaderCell>
                <Table.HeaderCell className={styles.lastCell}>{t('')}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {beneficiaries.length ? (
                beneficiaries.map(({ id, code, description, phoneNumber, email }, index) => (
                  <Table.Row key={id}>
                    <Table.Cell className={styles.firstCell}>{index + 1}</Table.Cell>
                    <Table.Cell className={styles.cell}>{code}</Table.Cell>
                    <Table.Cell className={styles.cell}>{description}</Table.Cell>
                    <Table.Cell className={styles.cell}>{phoneNumber}</Table.Cell>
                    <Table.Cell className={styles.cell}>{email}</Table.Cell>
                    <Table.Cell className={styles.lastCell}>
                      <div className={styles.tableCellIcons}>
                        {!viewMode && (
                          <>
                            <Button onClick={() => setDeletingBeneficiaryIndex(index)} secondary isSquare>
                              <Icon name="ic_delete" color={color('typography', 'light')} />
                            </Button>
                            <Button onClick={() => editBeneficiary(index)} secondary isSquare>
                              <Icon name="ic_edit" color={color('typography', 'light')} />
                            </Button>
                          </>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <EmptyTableContent title={t('There is no beneficiary added')} />
              )}
            </Table.Body>
            {!!beneficiaries.length && <TableFooter page={1} totalCount={beneficiaries.length} />}
          </Table.Grid>
        </Table>
      </TableWrapper>
      <BeneficiaryPopup index={selectedBeneficiaryIndex} onClose={closeAddPopup} onChange={handleChange} type={type} />

      <AdditionalFields tab={Tab.BENEFICIARY} configNamePrefix={FieldPrefix.lpco} />

      {typeof deletingBeneficiaryIndex === 'number' && (
        <ConfirmationModal
          onCancel={() => setDeletingBeneficiaryIndex(null)}
          type={ConfirmationModalType.Deletion}
          title={t('Delete Beneficiary')}
          onAccept={deleteBeneficiary}
          cancelText={t('Cancel')}
          acceptText={t('Delete')}
          id="confirmation-modal"
          isOpen
        >
          {t('Are you sure you want to delete Beneficiary ?')}
        </ConfirmationModal>
      )}
    </>
  );
};

export default memo(Beneficiaries);
